import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Select,
    NumberInput,
    NumberInputField,
    Button,
    VStack,
    HStack,
    Text,
    useToast,
} from '@chakra-ui/react';
import { LuPlus } from 'react-icons/lu';
import axios from 'axios';

const CronJobForm = ({ isOpen, onClose, isEditing, selectedJob, onJobCreated }) => {
    const toast = useToast();
    const [formData, setFormData] = useState({
        name: selectedJob?.name || '',
        description: selectedJob?.description || '',
        url: selectedJob?.url || '',
        schedule: selectedJob?.schedule || '',
        cron_exp: selectedJob?.cron_exp || "",
        timeout: selectedJob?.timeout || 30,
        retryAttempts: selectedJob?.retryAttempts || 0,
        schedule_type: selectedJob?.schedule_type || ""
    });
    const [loading, setLoading] = useState(false);

    const scheduleOptions = [
        { value: '*/5 * * * *', label: 'Every 5 minutes', type: "s" },
        { value: '*/15 * * * *', label: 'Every 15 minutes', type: "s" },
        { value: '*/30 * * * *', label: 'Every 30 minutes', type: "s" },
        { value: '0 * * * *', label: 'Every hour', type: "s" },
        { value: '0 0 * * *', label: 'Daily at midnight', type: "s" },
        { value: '0 2 * * *', label: 'Daily at 2:00 AM', type: "s" },
        { value: '0 6 * * *', label: 'Daily at 6:00 AM', type: "s" },
        { value: '0 12 * * *', label: 'Daily at noon', type: "s" },
        { value: '0 9 * * 1', label: 'Weekly on Monday at 9:00 AM', type: "s" },
        { value: '0 0 1 * *', label: 'Monthly on the 1st', type: "s" },
        { value: 'c', label: 'Custom Schedule', type: "c" },
    ];

    // Update form data when selectedJob changes
    React.useEffect(() => {
        if (selectedJob) {
            setFormData({
                name: selectedJob.name || '',
                description: selectedJob.description || '',
                url: selectedJob.url || '',
                // For custom schedules, set the actual cron expression in 'schedule' field
                // and set 'cron_exp' to 'c' to select "Custom Schedule" in dropdown
                schedule: selectedJob.schedule_type === 'c' ? selectedJob.cron_exp : '',
                cron_exp: selectedJob.schedule_type === 'c' ? 'c' : selectedJob.cron_exp,
                timeout: selectedJob.timeout || 30,
                retryAttempts: selectedJob.retryAttempts || 0,
                schedule_type: selectedJob.schedule_type || ""
            });
        } else {
            setFormData({
                name: '',
                description: '',
                url: '',
                schedule: '',
                cron_exp: "",
                timeout: 30,
                retryAttempts: 0,
                schedule_type: ""
            });
        }
    }, [selectedJob]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleScheduleChange = (field, value) => {
        let found = scheduleOptions.find((item) => item?.value === value);
        setFormData(prev => ({
            ...prev,
            [field]: value,
            schedule_type: found?.type || ""
        }));
    };

    const handleCustomScheduleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        // Basic validation
        if (!formData.name || !formData.url || !formData.cron_exp) {
            toast({
                title: "Validation Error",
                description: "Please fill in all required fields.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Additional validation for custom schedule
        if (formData.schedule_type === "c" && !formData.schedule) {
            toast({
                title: "Validation Error",
                description: "Please enter a custom schedule expression.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);

        try {
            // Prepare the payload based on schedule type
            let payloadPost = {
                name: formData.name,
                description: formData.description,
                file: formData.url,
                schedule_type: formData.schedule_type,
                timeout: formData.timeout,
                retryAttempts: formData.retryAttempts
            };
            let payloadEdit = {
                name: formData.name,
                description: formData.description,
                file: formData.url,
                schedule_type: formData.schedule_type,
                timeout: formData.timeout,
                retryAttempts: formData.retryAttempts,
                id: selectedJob?.id
            };

            let payload = isEditing ? payloadEdit : payloadPost

            // Add the appropriate cron expression based on schedule type
            if (formData.schedule_type === "c") {
                payload.cron_exp = formData.schedule;
            } else {
                payload.cron_exp = formData.cron_exp;
            }

            // Choose endpoint and method based on editing state
            const url = isEditing
                ? `${process.env.REACT_APP_API_URL}/cron/update`
                : `${process.env.REACT_APP_API_URL}/cron/create`;

            const response = await axios.post(url, payload, {
                headers: {
                    "Content-Type": "application/json",
                    "x-access-token": localStorage.getItem("user"),
                },
            });

            if (response.data) {
                toast({
                    title: isEditing ? "CRON Job Updated" : "CRON Job Created",
                    description: `${formData.name} has been successfully ${isEditing ? 'updated' : 'created'}.`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });

                // Call parent callback to refresh the list
                if (onJobCreated) {
                    onJobCreated();
                }

                handleClose();
            }

        } catch (error) {
            console.error('Error with CRON job:', error);
            toast({
                title: "Error",
                description: error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} CRON job. Please try again.`,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            description: '',
            url: '',
            schedule: '',
            cron_exp: "",
            timeout: 30,
            retryAttempts: 0,
            schedule_type: ""
        });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{isEditing ? 'Edit CRON Job' : 'Add New CRON Job'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                        Configure a new scheduled HTTP request
                    </Text>

                    <VStack spacing={4}>
                        <HStack spacing={4} w="full">
                            <FormControl isRequired>
                                <FormLabel>Job Name</FormLabel>
                                <Input
                                    placeholder="e.g., Daily Backup"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                />
                            </FormControl>
                        </HStack>

                        <FormControl>
                            <FormLabel>Description</FormLabel>
                            <Input
                                placeholder="Brief description of what this job does"
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>File Name</FormLabel>
                            <Input
                                placeholder="api.text"
                                value={formData.url}
                                onChange={(e) => handleInputChange('url', e.target.value)}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Schedule</FormLabel>
                            <Select
                                placeholder="Select schedule"
                                value={formData.cron_exp}
                                onChange={(e) => handleScheduleChange('cron_exp', e.target.value)}
                            >
                                {scheduleOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {formData.schedule_type === "c" && (
                            <FormControl isRequired>
                                <FormLabel>Custom Schedule</FormLabel>
                                <Input
                                    placeholder="* * * * * (minute hour day month weekday)"
                                    value={formData.schedule}
                                    onChange={(e) => handleCustomScheduleInputChange('schedule', e.target.value)}
                                />
                            </FormControl>
                        )}

                        <HStack spacing={4} w="full">
                            <FormControl>
                                <FormLabel>Timeout (seconds)</FormLabel>
                                <NumberInput
                                    min={1}
                                    max={300}
                                    value={formData.timeout}
                                    onChange={(value) => handleInputChange('timeout', parseInt(value) || 30)}
                                >
                                    <NumberInputField />
                                </NumberInput>
                            </FormControl>
                            <FormControl>
                                <FormLabel>Retry Attempts</FormLabel>
                                <NumberInput
                                    min={0}
                                    max={10}
                                    value={formData.retryAttempts}
                                    onChange={(value) => handleInputChange('retryAttempts', parseInt(value) || 0)}
                                >
                                    <NumberInputField />
                                </NumberInput>
                            </FormControl>
                        </HStack>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <Button mr={3} onClick={handleClose} size={"sm"} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="blue"
                        leftIcon={<LuPlus />}
                        onClick={handleSubmit}
                        size={"sm"}
                        isLoading={loading}
                        loadingText={isEditing ? 'Updating...' : 'Creating...'}
                    >
                        {isEditing ? 'Update Job' : 'Create Job'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CronJobForm;